import { HttpHeader } from '@/model';
import {
  ProposedShipmentRateResponse,
  ScaffoldRoutePlanPayload,
} from '@/model/routeBuilder';
import {
  RouteBuilderPhase2Request,
  RoutePlanBuilderView,
  RoutePlanProposedShipment,
  RoutePlanResponse,
  RoutePlanSummary,
} from '@/model/routeBuilder/phase2';

import { BaseHttpController } from './BaseHttpController';

export class RoutePlanController extends BaseHttpController {
  static readonly ENDPOINT: string = 'shipment-planning/route-plan';

  constructor() {
    super(RoutePlanController.ENDPOINT);
  }

  async createRoutePlan(
    routeBuilderRequest: RouteBuilderPhase2Request,
  ): Promise<RoutePlanResponse> {
    const response = await this.http.post<RoutePlanResponse>(
      '',
      routeBuilderRequest,
      {
        headers: {
          [HttpHeader.SOURCE]: 'Route Builder',
        },
      },
    );

    return new RoutePlanResponse(response.data);
  }

  async findRoutePlans(): Promise<RoutePlanSummary[]> {
    const response = await this.http.get<RoutePlanSummary[]>('');
    return response.data.map((s) => new RoutePlanSummary(s));
  }

  async findRoutePlan(id: number): Promise<RoutePlanSummary> {
    const response = await this.http.get<RoutePlanSummary>(`${id}`);
    return new RoutePlanSummary(response.data);
  }

  /** Persist an unfinished plan without materializing shipments. Returns the plan id. */
  async saveDraft(
    routePlanId: number | undefined,
    proposedShipments: RoutePlanProposedShipment[],
  ): Promise<number> {
    const response = await this.http.post<{ routePlanId: number }>('draft', {
      routePlanId,
      proposedShipments,
    });
    return response.data.routePlanId;
  }

  /** Load a saved draft back into the builder. */
  async loadForBuilder(id: number): Promise<RoutePlanBuilderView> {
    const response = await this.http.get<RoutePlanBuilderView>(`${id}/builder`);
    return new RoutePlanBuilderView(response.data);
  }

  /** Delete a route plan. The backend rejects deleting an EXECUTED plan (400). */
  async deleteRoutePlan(id: number): Promise<void> {
    await this.http.delete(`${id}`);
  }

  async scaffoldRoutePlan(
    payload: ScaffoldRoutePlanPayload,
  ): Promise<RoutePlanProposedShipment[]> {
    const response = await this.http.post<RoutePlanProposedShipment[]>(
      'scaffold',
      payload,
    );

    return response.data.map((s) => new RoutePlanProposedShipment(s));
  }

  async rateRoutePlanProposedShipment(
    proposedShipment: RoutePlanProposedShipment,
  ): Promise<ProposedShipmentRateResponse> {
    const response = await this.http.post<ProposedShipmentRateResponse>(
      'shipment/rate',
      { proposedShipment },
    );

    return new ProposedShipmentRateResponse(response.data);
  }
}
